import React from 'react';
import AgentSeat from './AgentSeat';
import { Sparkles, Loader2, FolderOpen } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import AiAssist from './AiAssist';
import AutoTextarea from '../ui/AutoTextarea';

/**
 * STAGE 1 — SCRIPT. The page where the creator says what the film is about.
 *
 * PRODUCT.md is explicit that this stage is a screenwriter *asking what the video is about — who, what, where,
 * why* and proposing fills for the gaps. That is a writing surface, not a form field: it was a single-line
 * `<input>` wedged between a template chip row and a length picker in a 360px rail, which invited six words
 * and gave the drafter almost nothing to work with.
 *
 * THE TEMPLATE CHIPS ARE GONE (owner 2026-08-14): "not required, as it can be driven by prompt". A row of
 * genre tropes (CEO Romance, Revenge & Redemption…) is a worse input than a sentence in the creator's own
 * words, and it pushed the actual idea below the fold. The genre KB still shapes the draft — it is retrieved
 * from what they WRITE rather than picked from a list.
 *
 * Presentational only (SteveJobs layering): everything arrives as props.
 */
export interface StoryStageProps {
  /**
   * THIS EPISODE'S NAME (owner 2026-08-24). Empty = unnamed, which is a real state: the server resolves an
   * unnamed episode to `<project> ep.<n>` on read, so the default keeps tracking a project rename instead of
   * freezing a copy of it into the document.
   */
  episodeTitle: string;
  onEpisodeTitleChange: (v: string) => void;
  /** Which episode this is, and of what — used for the placeholder that shows the name it would get. */
  episodeNumber?: number;
  projectTitle?: string;
  idea: string;
  onIdeaChange: (v: string) => void;
  /** The stream's own example, so a micro-drama author is never prompted with an ad. */
  placeholder: string;
  /** Draft the idea into shots. Disabled until there is something to draft. */
  onDraft: () => void;
  drafting?: boolean;
  /**
   * Label for the primary action. NOT "draft into shots": this stage produces a SCRIPT the creator approves,
   * and shots are drafted later from it (RF3-STORY). A series episode continues rather than starts.
   */
  draftLabel?: string;
  /** Shown under the action: what happens next, or why it cannot run yet. */
  hint?: string | null;
  /**
   * WHAT THIS EPISODE INHERITS from the micro-drama it belongs to (owner 2026-08-27: the two levels must be
   * separated, *"and parent info is inherited — right now we don't have that clear separation at episode
   * level, almost like creating everything from scratch"*).
   *
   * Shown as a READ-ONLY summary rather than re-asked. The plot library moved to the project entirely: which
   * plot shape a show has is a decision about the SHOW, and asking it per episode is what made an episode
   * feel like a fresh start.
   */
  inherited?: {
    projectTitle?: string;
    plotLabel?: string;
    premise?: string;
    era?: string;
    setting?: string;
    characters?: string;
    /** The cliffhanger the previous episode ended on — what THIS one has to pay off. */
    continuesFrom?: string;
  };
  /** Where the creator goes to change any of it. */
  onOpenProject?: () => void;
  /** The writer bench (owner 2026-08-15): who develops the script — the choice shapes its STYLE. */
  writers?: Array<{ agentId: string; name: string; flavor: string; hint: string }>;
  writerId?: string;
  /** The project this episode belongs to, so an assist writes INSIDE its plot and world rather than beside it. */
  seriesId?: string | null;
  onNeedsTopUp?: () => void;
  /**
   * WHAT THIS EPISODE NARROWS TO inside the project's world (RF3-LEVEL C1, owner 2026-08-24: at episode level
   * *"the script and with which characters, and where, inheriting from project-level"*). Both optional: empty
   * does not mean a blank world, it means the project's — which is why the project's own answers are the
   * PLACEHOLDERS rather than being copied in as values that would then stop tracking a project edit.
   */
  setting: string;
  onSettingChange: (v: string) => void;
  featuring: string;
  onFeaturingChange: (v: string) => void;
  /** The project's world + cast, shown as what these fields inherit. */
  inheritedSetting?: string;
  inheritedCast?: string;
  /** Put this writer on the production. Returning the recruit's promise lets the seat show it working. */
  onPickWriter?: (agentId: string) => void | Promise<void>;
}

/** One box treatment for the page's secondary fields — the same one the Project page uses. */
const FIELD = `mt-1 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 ${TYPE.manuscript}
               text-secondary-900 outline-none transition-colors placeholder:text-secondary-400
               focus:border-secondary-400 dark:border-secondary-600 dark:bg-secondary-900 dark:text-white
               dark:placeholder:text-secondary-600 dark:focus:border-secondary-500`;

const StoryStage: React.FC<StoryStageProps> = ({
  episodeTitle, onEpisodeTitleChange, episodeNumber = 1, projectTitle,
  idea, onIdeaChange, placeholder, onDraft, drafting, draftLabel = 'Develop this into a script', hint,
  inherited, onOpenProject, writers, writerId, onPickWriter, seriesId, onNeedsTopUp,
  setting, onSettingChange, featuring, onFeaturingChange, inheritedSetting, inheritedCast,
}) => {
  const ready = !!idea.trim();
  return (
    // One column. The script is not a second panel beside this one — it is what this becomes, so it flows
    // BELOW (see ScriptPreview). The reading width is set by the page, not by this block.
    <div className="ms-page w-full">
      {/* The Studio's type scale, not this page's own idea of a heading: Story used to open at 20/24px while
          every other stage headed its panels at 14px, which is exactly what read as "bigger fonts" here. */}
      {/* EPISODE LEVEL (RF3-LEVEL). The plot, the world and the cast are the PROJECT's — stated once on the
          Project page and inherited here. What this page asks for is what happens in THIS episode. */}
      {/*
        WHAT YOU ALREADY HAVE, stated before what you must write (owner 2026-08-27). The separation was real in
        the DATA — the project owned the plot, world and cast, and the episode's fields fell back to them — but
        invisible in the UI, so an episode still read as a blank page. Naming the inheritance out loud is what
        turns "start from scratch" into "continue the show".
      */}
      {inherited && (
        <section
          data-testid="inherited-from-project"
          className="mb-4 rounded-xl border border-secondary-200 p-3 dark:border-secondary-600"
          aria-label="What this episode inherits"
        >
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>From your series</span>
            <span className={`min-w-0 flex-1 truncate ${TYPE.control} font-medium text-secondary-900 dark:text-white`}>
              {inherited.projectTitle || 'Untitled series'}
            </span>
            {onOpenProject && (
              <button
                type="button"
                onClick={onOpenProject}
                data-testid="open-project"
                className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 ${TYPE.meta}
                           text-secondary-500 transition-colors hover:bg-secondary-100 hover:text-secondary-800
                           dark:text-secondary-400 dark:hover:bg-secondary-800`}
              >
                <FolderOpen className="h-3 w-3" aria-hidden /> Edit series
              </button>
            )}
          </div>
          <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-2">
            {([
              ['Plot', inherited.plotLabel || inherited.premise],
              ['When', inherited.era],
              ['Where', inherited.setting],
              ['Cast', inherited.characters],
            ] as Array<[string, string | undefined]>)
              .filter(([, v]) => !!(v || '').trim())
              .map(([k, v]) => (
                <div key={k} className="min-w-0">
                  <dt className={`${TYPE.meta} text-secondary-400`}>{k}</dt>
                  <dd className={`truncate ${TYPE.control} text-secondary-700 dark:text-secondary-300`} title={v}>{v}</dd>
                </div>
              ))}
          </dl>
          {/* The one inherited fact that is not a setting but an OBLIGATION: last episode's cliffhanger is
              what this episode has to pay off, so it is stated separately and in full. */}
          {inherited.continuesFrom && (
            <p className={`mt-2 border-t border-secondary-200 pt-2 ${TYPE.control} leading-relaxed
                           text-secondary-600 dark:border-secondary-600 dark:text-secondary-300`}>
              <span className={`${TYPE.meta} text-secondary-400`}>Picks up from: </span>
              {inherited.continuesFrom}
            </p>
          )}
        </section>
      )}

      <h2 className={`${TYPE.stageTitle} text-secondary-900 dark:text-white`}>This episode</h2>
      <p className={`mt-1 ${TYPE.body} text-secondary-500 dark:text-secondary-400`}>
        What happens in it. The series&apos; plot, world and cast come with it — your writer already has
        those, so a few sentences on this episode is plenty.
      </p>

      <label className="mt-4 block">
        <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>Episode title</span>
        <input
          value={episodeTitle}
          onChange={(e) => onEpisodeTitleChange(e.target.value)}
          data-testid="episode-title"
          /* The placeholder is the name it ALREADY has — leaving it blank is a valid choice, not an omission,
             and the resolved default tracks a project rename. */
          placeholder={`${projectTitle || 'Untitled series'} ep.${episodeNumber}`}
          className={`mt-1 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 ${TYPE.manuscriptTitle}
                     text-secondary-900 outline-none transition-colors placeholder:text-secondary-400
                     focus:border-secondary-400 dark:border-secondary-600 dark:bg-secondary-900
                     dark:text-white dark:placeholder:text-secondary-600 dark:focus:border-secondary-500`}
        />
      </label>

      {/* The other way in, offered BEFORE the blank page (a creator with no idea should not have to stare
          at an empty field to find help below it). Writing stays primary; picking fills the field with an
          editable premise the writer then develops. */}
      {/* ONE ROW OF CONTROLS, ABOVE THE WRITING (owner 2026-08-23: *"'Your writer' should be a small icon and
          above the text area"*). Both are decisions you make BEFORE you type — where the idea starts, and
          whose hands develop it — so they belong together and above, not split either side of the field. Both
          are the same chip, for the same reason. */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {/* The episode's premise can be written for you too — same control, same rules as the project's
            fields, and it writes INSIDE the project's plot and world rather than inventing its own. */}
        <AiAssist
          field="idea"
          current={idea}
          seriesId={seriesId}
          onWrite={(text) => onIdeaChange(text)}
          onNeedsTopUp={onNeedsTopUp}
        />
        {!!writers?.length && onPickWriter && (
          /* THE SAME SEAT AS EVERY OTHER AGENT PICK (owner law 2026-08-16) — the hiring room behind it is
             unchanged; only its footprint on this page is. */
          <AgentSeat
            compact
            testId="writer-seat"
            label="Your writer"
            seatTitle="Choose your writer"
            hiredLabel="Your writer"
            verb="Recruit"
            candidates={writers.map((w) => ({ agentId: w.agentId, name: w.name, flavor: w.flavor, hint: w.hint }))}
            currentId={writerId || null}
            onRecruit={(agentId) => onPickWriter(agentId)}
          />
        )}
      </div>

      <AutoTextarea
        value={idea}
        onChange={onIdeaChange}
        placeholder={placeholder}
        aria-label="Your idea"
        data-testid="story-idea"
        minRows={6}
        className={`mt-3 block w-full rounded-xl border border-secondary-200 bg-white px-4 py-3 ${TYPE.manuscript}
                   text-secondary-900 outline-none transition-colors placeholder:text-secondary-400
                   focus:border-secondary-400 dark:border-secondary-600 dark:bg-secondary-900 dark:text-white
                   dark:placeholder:text-secondary-600 dark:focus:border-secondary-500`}
      />

      {/* WHERE + WHO, for THIS episode. Under the premise because they NARROW it, and shown with the
          project's own answers as placeholders — so leaving them empty visibly means "inherit" rather than
          "unknown". Typing here overrides for this episode only, and the project stays the default. */}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>Where</span>
            <span className={`${TYPE.meta} text-secondary-400`}>{setting.trim() ? 'this episode' : 'inherited'}</span>
            {/* Every field a creator writes in offers to write it (owner law). These two were missed because
                they INHERIT when empty, which reads as "already answered" — but overriding is exactly where a
                creator most wants help, since the answer has to be specific to this episode to be worth
                typing at all. */}
            <AiAssist
              field="episode_setting"
              current={setting}
              seriesId={seriesId}
              label={setting.trim() ? 'Improve' : 'Write it'}
              onWrite={onSettingChange}
              onNeedsTopUp={onNeedsTopUp}
            />
          </span>
          <AutoTextarea
            value={setting}
            onChange={onSettingChange}
            minRows={2}
            data-testid="episode-setting"
            placeholder={inheritedSetting || 'Wherever the series is set'}
            className={FIELD}
          />
        </label>
        <label className="block">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>Who is in it</span>
            <span className={`${TYPE.meta} text-secondary-400`}>{featuring.trim() ? 'this episode' : 'inherited'}</span>
            <AiAssist
              field="episode_cast"
              current={featuring}
              seriesId={seriesId}
              label={featuring.trim() ? 'Improve' : 'Write it'}
              onWrite={onFeaturingChange}
              onNeedsTopUp={onNeedsTopUp}
            />
          </span>
          <AutoTextarea
            value={featuring}
            onChange={onFeaturingChange}
            minRows={2}
            data-testid="episode-featuring"
            placeholder={inheritedCast || 'Whoever is in the series'}
            className={FIELD}
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onDraft}
          disabled={!ready || drafting}
          data-testid="story-draft"
          className={`inline-flex min-h-[34px] items-center gap-2 rounded-lg bg-secondary-900 px-3.5 ${TYPE.control} font-semibold
                     text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40
                     dark:bg-white dark:text-secondary-900`}
        >
          {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {drafting ? 'Drafting…' : draftLabel}
        </button>
        {hint && <span className={`${TYPE.control} text-secondary-500 dark:text-secondary-400`}>{hint}</span>}
      </div>
    </div>
  );
};

export default StoryStage;
